import React from 'react';
import Tree from './tree';
import { StaticQuery, graphql } from 'gatsby';
import styled from '@emotion/styled';
import config from '../../../config';
import { ExternalLink } from 'react-feather';

// eslint-disable-next-line no-unused-vars
const ListItem = styled(({ className, active, level, ...props }) => {
  return (
    <li className={className}>
      <a className={className} href={props.to} {...props} target="_blank" rel="noopener noreferrer">
        {props.children}
      </a>
    </li>
  );
})`

  display: flex;
  position: fixed;
  bottom: 0;
  justify-content: space-between;
  list-style: none;

  a {
    color: #5c6975;
    text-decoration: none;
    font-weight: ${({ level }) => (level === 0 ? 700 : 400)};
    padding: 0.45rem 0 0.45rem ${props => 2 + (props.level || 0) * 1}rem;
    display: block;
    position: relative;

    &:hover {
    }

    ${props =>
      props.active &&
      `
      border-color: rgb(230,236,241) !important;
      border-style: solid none solid solid;
      border-width: 1px 0px 1px 1px;
      background-color: #fff;
    `} // external link icon
    svg {
      float: right;
      margin-right: 1rem;
      margin-left: 0.5rem;
      margin-top: 0.13rem;
    }
  }

  a.privacy-link  {
    color: #5c6975;
    text-decoration: none;
    margin: 0.2rem 4rem;

    &:hover {
      color: #682BD3 !important;
    }

    @media (max-width: 1163px) {
      margin: 0.1rem 0.2rem;
      padding: 6px 24px !important;
    }

    svg {
      float: right;
      margin-right: 1rem;
    }
  }

  a.start-button  {
    display: block;
    overflow: visible;
    position: relative;
    text-align: center;
    width: 100% !important;
    z-index: 2;
    margin: 3rem 1.4rem;
    border: 0;
    padding: 6px 68px !important;
    color: #fff!important;
    cursor: pointer;
    border-radius: 4px;
    box-shadow: rgb(45 35 66 / 40%) 0 2px 4px, rgb(45 35 66 / 30%) 0 7px 13px -3px, rgb(58 65 111 / 50%) 0 -3px 0 inset;
    text-shadow: rgb(0 0 0 / 40%) 0 1px 0;
    font-size: 1.0625rem;
    font-weight: 500;
    text-transform: uppercase;
    background-color: #5957fc;
    -webkit-transition: background-color .3s ease-out;
    transition: background-color .3s ease-out;
    line-height: 2.5 !important;

    @media (max-width: 1163px) {
      margin: 3rem 0.2rem;
      padding: 6px 24px !important;
    }

    &:hover {
      box-shadow: 0 0 2px rgb(0 0 0 / 10%), 0 6px 6px rgb(0 0 0 / 8%), 0 5px 6px rgb(0 0 0 / 8%), 0 6px 15px rgb(0 0 0 / 8%);
    }
  }

  /* Improved sidebar animation styles */

  /* Title container with flex layout for proper alignment */
  .node-title-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .node-title-container a {
    flex-grow: 1;
  }

  /* Submenu container with animation */
  .submenu-container {
    overflow: hidden;
    transition: max-height 0.3s ease, opacity 0.2s ease;
  }

  /* Collapsed state */
  .submenu-container.collapsed {
    max-height: 0;
    opacity: 0;
  }

  /* Expanded state */
  .submenu-container.expanded {
    max-height: 1000px; /* A reasonably large value */
    opacity: 1;
  }

  /* Arrow rotation animation */
  .collapser {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .collapser svg {
    transition: transform 0.3s ease;
  }

  .collapser[aria-expanded="true"] svg {
    transform: rotate(90deg);
  }

  .collapser[aria-expanded="false"] svg {
    transform: rotate(0deg);
  }

  /* Focus styles for accessibility */
  .collapser:focus {
    outline: 2px solid rgba(89, 87, 252, 0.5);
    border-radius: 4px;
  }

  /* Prevent the button from changing the layout when focused */
  .collapser:focus:not(:focus-visible) {
    outline: none;
  }
`;

const Sidebar = styled('aside')`
  width: 100%;
  height: 78vh;
  overflow: auto;
  position: fixed;
  padding-left: 0px;
  position: -webkit-sticky;
  position: -moz-sticky;
  position: sticky;
  top: 0;
  padding-right: 0;

  @media only screen and (max-width: 1023px) {
    width: 100%;
    height: 87vh;
  }

  @media (min-width: 767px) and (max-width: 1023px) {
    padding-left: 0;
  }

  @media only screen and (max-width: 767px) {
    padding-left: 0px;
    height: auto;
  }
`;

const Divider = styled(props => (
  <li {...props}>
    <hr />
  </li>
))`
  list-style: none;
  padding: 0.5rem 0;

  hr {
    margin: 0;
    padding: 0;
    border: 0;
    border-bottom: 1px solid #ede7f3;
  }
`;

const SidebarLayout = ({ location, locale }) => (
  <StaticQuery
    query={graphql`
      query {
        allMdx(sort: { fields: [frontmatter___order], order: ASC }) {
          edges {
            node {
              fields {
                slug
                title
                locale
              }
              frontmatter {
                title
                order
              }
            }
          }
        }
      }
    `}
    render={({ allMdx }) => {
      let hideElement = location && location.search.indexOf('inapp=true') >= 0;

      // Filter edges by current locale
      const currentLocale = locale || 'en-GB';
      const filteredEdges = allMdx.edges.filter(
        ({ node }) => node.fields.locale === currentLocale
      );

      return (
        <Sidebar>
          {config.sidebar.title ? (
            <div
              className={'sidebarTitle hiddenMobile'}
              dangerouslySetInnerHTML={{ __html: config.sidebar.title }}
            />
          ) : null}
          <ul className={'sideBarUL'}>
            <Tree edges={filteredEdges} locale={currentLocale} />
            {config.sidebar.links && config.sidebar.links.length > 0 }
            {config.sidebar.links.map((link, key) => {
              if (link.link !== '' && link.text !== '' && !hideElement) {
                return (
                  <ListItem className="start-button" key={key} to={link.link}>
                    {link.text}
                  </ListItem>
                );
              }
            })}
            {config.sidebar.linksNormal.map((link, key) => {
              if (link.link !== '' && link.text !== ''  && !hideElement) {
                return (
                  <ListItem className="privacy-link" key={key} to={link.link}>
                    {link.text}
                    <ExternalLink size={14} />
                  </ListItem>
                );
              }
            })}
          </ul>
        </Sidebar>
      );
    }}
  />
);

export default SidebarLayout;
